import React, { useEffect, useMemo, useState } from "react";
import PortalLayout from "../components/PortalLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { adminApi } from "../lib/portalApi";
import { toast } from "sonner";
import { ScrollText, RefreshCw, Search, Filter } from "lucide-react";

const Inner = () => {
  const [rows, setRows] = useState([]);
  const [actions, setActions] = useState([]);
  const [filter, setFilter] = useState({ action: "", actor: "", limit: 200 });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.auditLogs({
        action: filter.action || undefined,
        actor: filter.actor || undefined,
        limit: filter.limit,
      });
      setRows(r.logs || []);
      setActions(r.action_options || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const colored = useMemo(() => rows.map(r => {
    const a = String(r.action || "");
    let tone = "text-cream-dim";
    if (a.startsWith("user.") || a.startsWith("portal.") || a.startsWith("auth.")) tone = "text-electric";
    else if (a.startsWith("cyberwater.") || a.startsWith("auditiq.")) tone = "text-gold";
    else if (a.startsWith("devbuild.") || a.startsWith("settings.") || a.startsWith("seo.")) tone = "text-[color:var(--auditiq-warning)]";
    else if (a.includes("delete") || a.includes("reject") || a.includes("rollback")) tone = "text-[color:var(--auditiq-critical)]";
    return { ...r, _tone: tone };
  }), [rows]);

  return (
    <div className="space-y-6" data-testid="portal-audit-logs">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-electric flex items-center gap-2">
            <ScrollText size={12} /> System Audit Log
          </div>
          <h1 className="font-display text-3xl md:text-4xl mt-2">Audit Log Viewer</h1>
          <p className="text-cream-dim text-sm mt-2">Immutable record of user and system actions. Super Admin &amp; Auditor access.</p>
        </div>
        <Button onClick={load} className="btn-ghost-gold h-10 px-4 text-xs" disabled={loading}
          data-testid="audit-refresh-btn">
          <RefreshCw size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} />
          {loading ? "Loading…" : "Refresh"}
        </Button>
      </div>

      {/* Filters */}
      <div className="glass p-5 grid md:grid-cols-4 gap-3">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.22em] text-steel mb-1">Action</label>
          <Select value={filter.action || "__all"}
            onValueChange={v => setFilter(f => ({ ...f, action: v === "__all" ? "" : v }))}>
            <SelectTrigger className="bg-navy-deep border-gold-dim/40 text-cream" data-testid="audit-filter-action">
              <SelectValue placeholder="All actions" />
            </SelectTrigger>
            <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream max-h-[320px]">
              <SelectItem value="__all">All actions</SelectItem>
              {actions.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.22em] text-steel mb-1">Actor (email)</label>
          <Input value={filter.actor} onChange={e => setFilter(f => ({ ...f, actor: e.target.value }))}
            placeholder="e.g. admin@auditiq.ai" className="bg-navy-deep border-gold-dim/40"
            data-testid="audit-filter-actor" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.22em] text-steel mb-1">Limit</label>
          <Select value={String(filter.limit)}
            onValueChange={v => setFilter(f => ({ ...f, limit: Number(v) }))}>
            <SelectTrigger className="bg-navy-deep border-gold-dim/40 text-cream" data-testid="audit-filter-limit">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
              {[50, 100, 200, 500, 1000].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end gap-2">
          <Button onClick={load} className="btn-gold h-10 px-5 text-xs w-full" data-testid="audit-apply-filter">
            <Search size={14} className="mr-2" /> Apply
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="glass p-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-steel">Events returned</div>
          <div className="font-display text-2xl text-gold mt-1">{rows.length}</div>
        </div>
        <div className="glass p-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-steel">Distinct actions</div>
          <div className="font-display text-2xl text-electric mt-1">{actions.length}</div>
        </div>
        <div className="glass p-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-steel">Distinct actors</div>
          <div className="font-display text-2xl text-cream mt-1">
            {new Set(rows.map(r => r.actor)).size}
          </div>
        </div>
        <div className="glass p-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-steel">Most recent</div>
          <div className="text-cream text-sm mt-1">
            {rows.length ? new Date(rows[0].ts).toLocaleString() : "—"}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="glass p-0 overflow-x-auto">
        <table className="w-full text-sm" data-testid="audit-logs-table">
          <thead className="text-[11px] uppercase tracking-[0.2em] text-steel">
            <tr className="border-b border-gold-dim/20">
              <th className="text-left p-4">Timestamp</th>
              <th className="text-left p-4">Action</th>
              <th className="text-left p-4">Actor</th>
              <th className="text-left p-4">Meta</th>
            </tr>
          </thead>
          <tbody>
            {colored.map(r => (
              <tr key={r.id} className="border-b border-gold-dim/10 hover:bg-navy-soft/40">
                <td className="p-4 text-cream-dim font-mono text-xs whitespace-nowrap">
                  {new Date(r.ts).toLocaleString()}
                </td>
                <td className={`p-4 font-mono text-xs ${r._tone}`}>{r.action}</td>
                <td className="p-4 text-gold">{r.actor}</td>
                <td className="p-4 text-cream-dim font-mono text-[11px] max-w-xl">
                  <code className="break-all">{JSON.stringify(r.meta || {})}</code>
                </td>
              </tr>
            ))}
            {!rows.length && !loading && (
              <tr>
                <td colSpan={4} className="p-10 text-center text-cream-dim italic">
                  <Filter size={16} className="inline mr-2 opacity-50" />
                  No audit events match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const PortalAuditLogs = () => <PortalLayout><Inner /></PortalLayout>;
export default PortalAuditLogs;
